// Thin wrapper around `browser-image-compression` that exposes a
// byte-based API and returns a normalized result shape so callers
// can show savings in the UI.

import imageCompression from "browser-image-compression"

const DEFAULT_OPTIONS = {
  // Maximum width or height in pixels. Library keeps aspect ratio.
  maxWidthOrHeight: 800,
  // Maximum file size in bytes. Internally converted to MB for the lib.
  maxSizeBytes: 100 * 1024, // 100 KB
  // Starting JPEG/WebP quality (0..1).
  initialQuality: 0.85,
  // Hard floor for the iterative search.
  minQuality: 0.5,
  // Run the heavy work in a Web Worker so the UI stays responsive.
  useWebWorker: true,
  // Max iterations before giving up and returning the best so far.
  maxIteration: 10,
}

const bytesToMB = bytes => bytes / (1024 * 1024)

/**
 * Compress an image File in the browser using `browser-image-compression`.
 *
 * @param {File|Blob} file - Source image file.
 * @param {Object} [options] - See DEFAULT_OPTIONS. `maxSizeBytes` takes
 *   precedence over any `maxSizeMB` passed by the caller.
 * @param {(progress: number) => void} [onProgress] - Optional 0..100 progress
 *   callback forwarded to the underlying library.
 * @returns {Promise<{ file: File, originalSize: number, compressedSize: number, ratio: number, skipped: boolean }>}
 */
export const compressImage = async (
  file,
  options = {},
  onProgress
) => {
  if (!file || !file.type || !file.type.startsWith("image/")) {
    throw new Error("Not a valid image file")
  }

  const opts = { ...DEFAULT_OPTIONS, ...options }
  const originalSize = file.size

  // Already small enough — pass through unchanged. The library would
  // re-encode anyway, which can introduce quality loss on already-tiny files.
  if (originalSize <= opts.maxSizeBytes) {
    if (typeof onProgress === "function") onProgress(100)
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      ratio: 1,
      skipped: true,
    }
  }

  const compressed = await imageCompression(file, {
    maxSizeMB: Math.max(0.01, bytesToMB(opts.maxSizeBytes)),
    maxWidthOrHeight: opts.maxWidthOrHeight,
    initialQuality: opts.initialQuality,
    useWebWorker: opts.useWebWorker,
    maxIteration: opts.maxIteration,
    fileType: file.type, // preserve source mime (png/webp/jpeg)
    preserveExif: false,
    onProgress,
  })

  return {
    file: compressed,
    originalSize,
    compressedSize: compressed.size,
    ratio: compressed.size / originalSize,
    skipped: false,
  }
}

export const formatBytes = bytes => {
  if (!Number.isFinite(bytes) || bytes < 0) return "-"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}
